// 셀러 쿠폰 세그먼트 — 성과 API 1회 호출로 등록 수·사용 실적을 함께 판정 (기획 260915 정정: 유도 기준 = 등록 3개 미만)
import { sellerCouponApi, SELLER_COUPON_BASE } from '@/api/SellerCouponApi'
import { hasMenuAuth } from '@/utils/menuAuth'

// 유도(카드·배너) 노출 상한 — 등록 쿠폰이 이 수 이상이면 유도 종료
export const COUPON_GUIDE_MAX_COUPONS = 3

// 판정 캐시 TTL — 화면을 오가는 동안 재호출을 막되, 사용 발생 등 상태 변화가 화면 재진입 시 반영되게.
// 재검사 시점은 각 게이트 컴포넌트의 마운트(immediate watcher)다 — 한 화면에 머무는 동안의 실시간 전환은 스펙 아님
const SEGMENT_TTL_MS = 5 * 60 * 1000

const state = {
  loadedFor: null as number | null, // 판정을 로드한 sellerNo — 대리 로그인/셀러 전환 시 재조회
  loadedAt: 0,
  epoch: 0, // 무효화 세대 — 진행 중이던 응답이 무효화 이후 stale 커밋되는 것 방지
  perf: null as Coupon.Performance | null,
  showcase: null as Coupon.ShowcaseCase[] | null,
  showcaseLoadedFor: null as number | null, // showcase 를 로드한 sellerNo
}

// 진행 중 요청 디듀프 — 여러 게이트 컴포넌트가 동시에 dispatch 해도 호출은 1번, 응답 경합 없음
let inflight: Promise<void> | null = null
let inflightEpoch = -1

// showcase 진행 중 요청 디듀프 (loadCouponSegment 의 inflight 와 동일 패턴, epoch 없이 sellerNo 로 stale 판정)
let showcaseInflight: Promise<void> | null = null
let showcaseInflightSeller: number | null = null // 진행 중 요청이 어느 셀러 것인지 — 다른 셀러 요청과 합류하지 않도록

const getters = {
  // 판정은 "현재 셀러로 로드된 경우"에만 유효
  perf: (state, _getters, _rootState, rootGetters): Coupon.Performance | null =>
    state.loadedFor === rootGetters.sellerNo ? state.perf : null,
  couponCount: (_state, getters): number | null => (getters.perf ? getters.perf.coupon_count : null),
  hasUsage: (_state, getters): boolean => !!getters.perf && getters.perf.lifetime.use_order_count > 0,
  /** 대시보드 유도 카드 — 등록 3개 미만이면서 사용 실적이 없을 때 (사용 발생 시 성과 카드가 이어받음) */
  showPromoCard: (_state, getters): boolean =>
    getters.couponCount !== null && getters.couponCount < COUPON_GUIDE_MAX_COUPONS && !getters.hasUsage,
  /** 상품리스트·행사 유도 배너 — 등록 3개 미만이면 사용 여부와 무관하게 유지 */
  showPromoBanner: (_state, getters): boolean => getters.couponCount !== null && getters.couponCount < COUPON_GUIDE_MAX_COUPONS,
  /** 쿠폰 빈 목록 온보딩 — 등록 이력이 아예 없을 때만 */
  isS0: (_state, getters): boolean => getters.couponCount === 0,
  // showcase 도 "현재 셀러로 로드된 경우"에만 유효 (perf 와 동일 패턴)
  showcase: (state, _getters, _rootState, rootGetters): Coupon.ShowcaseCase[] | null =>
    state.showcaseLoadedFor === rootGetters.sellerNo ? state.showcase : null,
}

const mutations = {
  setPerformance(state, payload: { sellerNo: number; perf: Coupon.Performance }) {
    state.loadedFor = payload.sellerNo
    state.loadedAt = Date.now()
    state.perf = payload.perf
  },
  /** 쿠폰 등록 등으로 판정이 바뀌면 무효화 — 다음 dispatch 에서 재조회 (showcase 는 공용 데이터라 건드리지 않음) */
  invalidateSegment(state) {
    state.loadedFor = null
    state.loadedAt = 0
    state.perf = null
    state.epoch += 1 // 진행 중 응답의 커밋을 무효화
  },
  setShowcase(state, payload: { sellerNo: number; cases: Coupon.ShowcaseCase[] }) {
    state.showcaseLoadedFor = payload.sellerNo
    state.showcase = payload.cases
  },
}

const actions = {
  async loadCouponSegment({ state, commit, rootGetters }) {
    const sellerNo = rootGetters.sellerNo
    if (!sellerNo) return
    // 같은 셀러라도 TTL 경과 시 재조회 — 사용 발생·쿠폰 변화가 세션 내 반영되게
    if (state.loadedFor === sellerNo && Date.now() - state.loadedAt < SEGMENT_TTL_MS) return
    // 권한 게이트를 action 내부에서 수행 — 무권한 셀러가 403 → 전역 인터셉터 로그아웃으로 튕기는 것 방지
    if (!hasMenuAuth(rootGetters.menus, '/coupon')) return
    // 같은 세대의 진행 중 요청에만 합류 — 무효화(epoch 증가) 후에는 새 요청을 시작한다
    if (inflight && inflightEpoch === state.epoch) return inflight
    const myEpoch = state.epoch
    inflightEpoch = myEpoch
    inflight = (async () => {
      try {
        const res = await sellerCouponApi.get(`${SELLER_COUPON_BASE}/performance`, undefined, { silentError: true })
        if (state.epoch === myEpoch) commit('setPerformance', { sellerNo, perf: res.data.data })
      } catch {
        // 판정 실패 시 노출하지 않음(과노출 방지) — stale 커밋 방지를 위해 같은 세대일 때만 무효화
        if (state.epoch === myEpoch) commit('invalidateSegment')
      } finally {
        if (inflightEpoch === myEpoch) inflight = null
      }
    })()
    return inflight
  },

  /** S0 쇼케이스 로드 — 셀러당 1회, 실패해도 예외를 전파하지 않고 빈 배열로 고정(과노출 없음) */
  async loadShowcase({ state, commit, rootGetters }) {
    const sellerNo = rootGetters.sellerNo
    if (!sellerNo || state.showcaseLoadedFor === sellerNo) return
    if (!hasMenuAuth(rootGetters.menus, '/coupon')) return
    // 같은 셀러의 진행 중 요청에만 합류 — 여러 게이트 컴포넌트 동시 dispatch 시 호출 1번.
    // 다른 셀러(대리 로그인 전환 등) 요청이 진행 중이면 합류하지 않고 새로 시작한다 — 옛 요청의 stale 응답은 sellerNo 가드로 이미 버려짐
    if (showcaseInflight && showcaseInflightSeller === sellerNo) return showcaseInflight
    showcaseInflightSeller = sellerNo
    showcaseInflight = (async () => {
      try {
        const res = await sellerCouponApi.get(`${SELLER_COUPON_BASE}/showcase`, undefined, { silentError: true })
        // 응답 도착 시점에 셀러가 바뀌었으면(대리 로그인 전환 등) stale 응답을 버리고 showcaseLoadedFor 는 건드리지 않아 현재 셀러가 재조회하게 함
        if (rootGetters.sellerNo === sellerNo) commit('setShowcase', { sellerNo, cases: res.data.data || [] })
      } catch {
        if (rootGetters.sellerNo === sellerNo) commit('setShowcase', { sellerNo, cases: [] }) // 실패 시 기존 고정 문구 유지(과노출 없음)
      } finally {
        // 그 사이 다른 셀러가 새 요청을 시작했다면(이미 자기 것으로 덮어씀) 건드리지 않는다
        if (showcaseInflightSeller === sellerNo) {
          showcaseInflight = null
          showcaseInflightSeller = null
        }
      }
    })()
    return showcaseInflight
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
